import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calculator, GitBranch, ArrowRight, TrendingUp, Target, Info, AlertTriangle, Grid3X3, Settings, ChevronDown, ChevronUp, FlaskConical, BarChart3, Save, FileText, Loader2, FileSpreadsheet, User, Users, DollarSign, Clock, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Sankey, Rectangle, Layer, ResponsiveContainer } from 'recharts';
import { LEIMeasureInfo } from '../components/LEIMeasureInfo';
import LeiImpactCard from '../components/LeiImpactCard';
import { LEICalculatorDrawer } from '../components/LEICalculatorDrawer';
import { AnalysisModeToggle } from '../components/AnalysisModeToggle';
import { SaveScenarioDialog } from '../components/SaveScenarioDialog';
import CohortSelector from '../components/CohortSelector';
import BoardReadyButton from '../components/BoardReadyButton';
import BenchmarkComparison from '../components/BenchmarkComparison';
import NarrativeCard from '../components/analysis/NarrativeCard';
import { MeasureContributionChart, MeasureSharePieChart } from '../components/charts/MeasureContributionChart';
import { getLEIMeasures, getKPIs, calculateFullMatrix, getLEIKPIWeights } from '../lib/api';
import { formatCurrency, getCurrencySymbol, cn } from '../lib/utils';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { MaturitySliderWithGuide } from '../components/MaturitySliderWithGuide';
import { TransferFactorsCard, TransferRateResultsCard } from '../components/TransferFactorsCard';
import { ExecutionRiskCollapsible } from '../components/ExecutionRiskFactors';
import { toast } from 'sonner';
import api from '../lib/api';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

// Error boundary for Sankey chart
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('Sankey chart error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

// Info button component for calculation breakdowns
const CalcInfoButton = ({ title, breakdown, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="ml-1 p-0.5 rounded-full hover:bg-slate-200 transition-colors inline-flex items-center justify-center">
        <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 p-4" align="start">
      <h4 className="font-semibold text-slate-900 mb-2">{title}</h4>
      {breakdown && (
        <div className="space-y-2 text-xs">
          {breakdown.formula && (
            <div className="p-2 bg-slate-100 rounded font-mono text-slate-700">
              {breakdown.formula}
            </div>
          )}
          {children}
        </div>
      )}
    </PopoverContent>
  </Popover>
);

// Custom Sankey node component
const CustomSankeyNode = ({ x, y, width, height, index, payload }) => {
  const isKPI = payload.type === 'kpi';
  const color = isKPI ? '#1e3a5f' : '#0d9488';
  
  // Show full name without truncation
  const displayName = payload.name || '';
  
  return (
    <Layer key={`sankey-node-${index}`}>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        fillOpacity="0.9"
        rx={4}
        ry={4}
      />
      <text
        x={isKPI ? x + width + 12 : x - 12}
        y={y + height / 2}
        textAnchor={isKPI ? 'start' : 'end'}
        dominantBaseline="middle"
        fontSize={11}
        fill="#1e293b"
        className="sankey-node-label"
        fontWeight={500}
        style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
      >
        {displayName}
      </text>
    </Layer>
  );
};

// Custom Sankey link component
const CustomSankeyLink = ({ sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, index, payload }) => {
  const gradientId = `linkGradient${index}`;
  
  return (
    <Layer key={`sankey-link-${index}`}>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#0d9488" stopOpacity={0.5} />
          <stop offset="100%" stopColor="#1e3a5f" stopOpacity={0.5} />
        </linearGradient>
      </defs>
      <path
        d={`
          M${sourceX},${sourceY}
          C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
        `}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={Math.max(linkWidth, 2)}
        strokeOpacity={0.6}
      />
    </Layer>
  );
};

export const FullMatrixPage = () => {
  const { setResults: setGlobalResults, setConfiguration: setGlobalConfig, currency, setNarrative } = useApp();
  const { profileDefaults, hasProfileDefaults, viewAsOrg } = useAuth();
  
  const [leiMeasures, setLEIMeasures] = useState([]);
  const [kpis, setKPIs] = useState([]);
  const [weightMatrix, setWeightMatrix] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [results, setResults] = useState(null);
  const [resultsStale, setResultsStale] = useState(false); // Track if inputs changed since last calculation
  const [viewMode, setViewMode] = useState('sankey'); // sankey, by-kpi, by-measure, weights
  const [bulkValue, setBulkValue] = useState(2.00);
  const [showDampeningDetails, setShowDampeningDetails] = useState(true);
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [analysisMode, setAnalysisMode] = useState('simple'); // 'simple', 'advanced', 'import'
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [profileDefaultsApplied, setProfileDefaultsApplied] = useState(false);
  // Maturity Assessment auto-sync — mirrors Single Measure UX.
  const navigate = useNavigate();
  const [latestMaturityAssessment, setLatestMaturityAssessment] = useState(null);
  const [maturityAssessmentApplied, setMaturityAssessmentApplied] = useState(false);
  const [maturityOverridden, setMaturityOverridden] = useState(false);
  const [isDetailedBreakdownExpanded, setIsDetailedBreakdownExpanded] = useState(false);  // Collapsed by default
  const [isImpactAdjustmentsExpanded, setIsImpactAdjustmentsExpanded] = useState(false);  // Collapsed by default
  const [importedBaselines, setImportedBaselines] = useState(null);
  const [isLoadingBaselines, setIsLoadingBaselines] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const configRef = useRef(null);
  const resultsRef = useRef(null);

  // Configuration
  const [measureInputs, setMeasureInputs] = useState([]);
  const [config, setConfig] = useState({
    elasticity_scenario: 'moderate',
    intervention_cost: 500000,
    current_maturity: 50,
    dampening_factors: {
      adoption_speed: 0.3,       // 30% risk = lower dampening
      leadership_alignment: 0.2, // 20% risk = lower dampening
      operational_readiness: 0.25, // 25% risk = lower dampening
    },
    headcount: 1000,
    revenue_per_employee: 150000,
    // Transfer rate factors
    manager_support: 'informed',
    job_alignment: 'role_relevant',
    time_to_apply: '2_4_weeks',
    learner_population: null,
  });
  
  // Apply profile defaults on mount
  useEffect(() => {
    if (hasProfileDefaults && !profileDefaultsApplied) {
      setConfig(prev => ({
        ...prev,
        elasticity_scenario: profileDefaults.elasticity_scenario,
        intervention_cost: profileDefaults.intervention_cost,
        current_maturity: profileDefaults.current_maturity,
        dampening_factors: profileDefaults.dampening_factors,
        headcount: profileDefaults.headcount,
        revenue_per_employee: profileDefaults.revenue_per_employee,
      }));
      setProfileDefaultsApplied(true);
    }
  }, [hasProfileDefaults, profileDefaults, profileDefaultsApplied]);

  // Auto-adopt the latest Maturity Assessment as the slider's
  // source of truth — same behavior as Single Measure & Portfolio.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/maturity-assessment/latest');
        if (cancelled) return;
        const a = res?.data?.assessment;
        if (!a || a.overall_score == null) return;
        setLatestMaturityAssessment(a);
        if (!maturityAssessmentApplied && !maturityOverridden) {
          setConfig(prev => ({ ...prev, current_maturity: Math.round(a.overall_score) }));
          setMaturityAssessmentApplied(true);
        }
      } catch {
        // Non-fatal — slider falls back to profile default.
      }
    })();
    return () => { cancelled = true; };
  }, [maturityAssessmentApplied, maturityOverridden]);

  // Mark results as stale when measureInputs or config changes
  const handleMeasureInputChange = (index, field, value) => {
    setMeasureInputs(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    if (results) {
      setResultsStale(true);
    }
  };

  const handleConfigChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    if (results) {
      setResultsStale(true);
    }
  };

  const handleDampeningChange = (factor, value) => {
    setConfig(prev => ({
      ...prev,
      dampening_factors: { ...prev.dampening_factors, [factor]: value }
    }));
    if (results) {
      setResultsStale(true);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [leiRes, kpiRes, weightsRes] = await Promise.all([
          getLEIMeasures(),
          getKPIs(),
          getLEIKPIWeights(),
        ]);
        setLEIMeasures(leiRes.data);
        setKPIs(kpiRes.data);
        setWeightMatrix(weightsRes.data);
        
        setMeasureInputs(
          leiRes.data.map((m) => ({
            lei_measure_id: m.id,
            lei_improvement: 2,
            enabled: true,
          }))
        );
      } catch (error) {
        logger.error('Error loading data:', error);
        toast.error('Failed to load reference data');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);
  
  // Fetch imported baselines when Import mode is selected
  useEffect(() => {
    const fetchImportedBaselines = async () => {
      if (analysisMode === 'import') {
        setIsLoadingBaselines(true);
        try {
          const response = await api.get('/import/data/lei-baselines/latest');
          setImportedBaselines(response.data);
          
          // Auto-fill measure inputs with imported baselines
          if (response.data.baselines) {
            setMeasureInputs(prev => prev.map(m => {
              const baseline = response.data.baselines[m.lei_measure_id];
              if (baseline?.calculated_score) {
                return { ...m, imported_score: baseline.calculated_score };
              }
              return m;
            }));
          }
        } catch (error) {
          logger.error('Failed to fetch imported baselines:', error);
          toast.error('Failed to load imported data. Please check Data Import.');
        } finally {
          setIsLoadingBaselines(false);
        }
      }
    };
    
    fetchImportedBaselines();
  }, [analysisMode]);
  
  // Handle mode change
  const handleModeChange = (newMode) => {
    setAnalysisMode(newMode);
    setIsAdvancedMode(newMode === 'advanced');
  };

  const updateMeasureInput = (measureId, field, value) => {
    setMeasureInputs((prev) =>
      prev.map((m) =>
        m.lei_measure_id === measureId ? { ...m, [field]: value } : m
      )
    );
    if (results) {
      setResultsStale(true);
    }
  };

  const openCalculator = (measure) => {
    setSelectedMeasure(measure);
    setCalculatorOpen(true);
  };

  const handleCalculatorApply = (improvement) => {
    const improvementValue = typeof improvement === 'number' ? improvement : 0;
    if (selectedMeasure) {
      updateMeasureInput(selectedMeasure.id, 'lei_improvement', improvementValue);
      updateMeasureInput(selectedMeasure.id, 'enabled', true);
      toast.success(`Applied ${improvementValue.toFixed(1)}% improvement to ${selectedMeasure.name}`);
    }
  };

  const applyBulkValue = () => {
    const value = parseFloat(bulkValue) || 0;
    if (value <= 0) {
      // Enable all measures with 0% - the inline warning will show automatically
      setMeasureInputs((prev) =>
        prev.map((m) => ({ 
          ...m, 
          enabled: true,
          lei_improvement: 0 
        }))
      );
      if (results) setResultsStale(true);
      return;
    }
    setMeasureInputs((prev) =>
      prev.map((m) => ({ 
        ...m, 
        enabled: true,
        lei_improvement: value 
      }))
    );
    if (results) setResultsStale(true);
    toast.success(`Applied ${value}% to all measures`);
  };

  const scrollToConfig = () => {
    configRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Check if there are valid measures (enabled with improvement > 0)
  const hasValidMeasures = measureInputs.some((m) => m.enabled && m.lei_improvement > 0);

  const handleCalculate = async () => {
    if (!hasValidMeasures) {
      toast.error('You must enter at least one LEI measure with improvement > 0% to calculate ROI');
      return;
    }

    setIsCalculating(true);
    try {
      const response = await calculateFullMatrix({
        measures: measureInputs,
        ...config,
        organization_id: viewAsOrg?.id || undefined,
        scale_dampening_config: profileDefaults?.scale_dampening_config || null,
        industry_type: profileDefaults?.industry_type || 'other',
        learning_modality: profileDefaults?.primary_learning_modality || 'blended',
        time_lag_config: profileDefaults?.time_lag_config || null,
      });
      setResults(response.data);
      setResultsStale(false); // Clear stale flag after successful calculation
      // Sync with global context for saving
      setGlobalResults(response.data);
      setGlobalConfig({
        ...config,
        lei_improvement: measureInputs.filter(m => m.enabled).reduce((sum, m) => sum + m.lei_improvement, 0),
        kpi_ids: kpis.map(k => k.id),
      });
      toast.success('Full matrix analysis complete!');
      
      // Scroll to results after a brief delay for render
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error) {
      logger.error('Calculation error:', error);
      toast.error('Full matrix calculation failed');
    } finally {
      setIsCalculating(false);
    }
  };

  // Prepare Sankey data - limit to top contributors to avoid stack overflow
  const sankeyData = useMemo(() => {
    if (!results || !results.sankey_nodes || !results.sankey_links) return null;
    if (results.sankey_nodes.length === 0 || results.sankey_links.length === 0) return null;
    
    // Filter to only show significant links (top 20 by financial impact)
    const sortedLinks = [...results.sankey_links]
      .filter(link => link.value > 0 && link.financial_impact > 0)
      .sort((a, b) => b.financial_impact - a.financial_impact)
      .slice(0, 20); // Limit to top 20 links to prevent stack overflow
    
    if (sortedLinks.length === 0) return null;
    
    // Get unique node IDs from the filtered links
    const usedNodeIds = new Set();
    sortedLinks.forEach(link => {
      usedNodeIds.add(link.source);
      usedNodeIds.add(link.target);
    });
    
    // Filter and separate nodes into measures and KPIs
    const measureNodes = results.sankey_nodes
      .filter(n => n.type === 'measure' && usedNodeIds.has(n.id));
    const kpiNodes = results.sankey_nodes
      .filter(n => n.type === 'kpi' && usedNodeIds.has(n.id));
    
    // Reorder nodes: measures first, then KPIs
    const orderedNodes = [...measureNodes, ...kpiNodes];
    
    if (orderedNodes.length === 0) return null;
    
    // Create a map of node IDs to their new indices
    const nodeIdToIndex = {};
    orderedNodes.forEach((node, index) => {
      nodeIdToIndex[node.id] = index;
    });
    
    // Convert string IDs to numeric indices for Recharts Sankey
    const links = sortedLinks
      .filter(link => {
        const sourceExists = nodeIdToIndex[link.source] !== undefined;
        const targetExists = nodeIdToIndex[link.target] !== undefined;
        return sourceExists && targetExists;
      })
      .map(link => ({
        source: nodeIdToIndex[link.source],
        target: nodeIdToIndex[link.target],
        value: Math.max(link.value, 1),
        financial_impact: link.financial_impact
      }));
    
    if (links.length === 0) return null;
    
    return {
      nodes: orderedNodes,
      links: links
    };
  }, [results]);

  const enabledCount = measureInputs.filter((m) => m.enabled).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Full LEI-to-KPI Impact Matrix"
        description="Cross-cut analysis across measures, factors, and segments."
        theme={__pageTheme}
        icon={GitBranch}
        testId="full-matrix-hero"
        actions={(
          <div className="flex items-center gap-2">
            <AnalysisModeToggle
              mode={analysisMode}
              onModeChange={handleModeChange}
            />
            <Link
              to="/roe-reference#pe-validated"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 text-xs font-medium backdrop-blur-sm transition-colors"
              data-testid="full-matrix-hero-reference-btn"
            >
              <BookOpen className="h-3.5 w-3.5" /> ROE Reference
            </Link>
          </div>
        )}
      />
    )}>
    <TooltipProvider>
      <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6 animate-fade-in">
        {/* Header */}
        
        {/* Profile Defaults Banner */}
        {hasProfileDefaults && profileDefaultsApplied && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="profile-defaults-banner-matrix">
            <User className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-blue-800">
                <span className="font-medium">Using your profile defaults.</span>{' '}
                <span className="text-blue-600">Dampening factors, maturity, headcount, and other settings are pre-filled from your profile. You can adjust them below for this analysis.</span>
              </p>
            </div>
            <Link to="/profile">
              <Button variant="ghost" size="sm" className="text-blue-700 hover:text-blue-900 hover:bg-blue-100">
                Edit Profile
              </Button>
            </Link>
          </div>
        )}

        {/* Import Mode Banner */}
        {analysisMode === 'import' && (
          <div className="flex items-center gap-3 p-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="import-mode-banner-matrix">
            <FileSpreadsheet className="h-5 w-5 text-teal-600 flex-shrink-0" />
            <div className="flex-1">
              {isLoadingBaselines ? (
                <p className="text-sm text-teal-800">Loading imported baselines...</p>
              ) : importedBaselines?.count > 0 ? (
                <p className="text-sm text-teal-800">
                  <span className="font-medium">Using imported baselines.</span>{' '}
                  <span className="text-teal-600">{importedBaselines.count} measures available. Baseline scores shown next to each measure.</span>
                </p>
              ) : (
                <p className="text-sm text-amber-800">
                  <span className="font-medium">No imported data found.</span>{' '}
                  <span className="text-amber-600">Go to Data Import to upload your LEI baselines first.</span>
                </p>
              )}
            </div>
            <Link to="/data-management">
              <Button variant="ghost" size="sm" className="text-teal-700 hover:text-teal-900 hover:bg-teal-100">
                Data Import
              </Button>
            </Link>
          </div>
        )}

        {/* Target Cohort Selection */}
        <Card className="border-teal-200 bg-gradient-to-r from-teal-50/50 to-emerald-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg">Target Cohort</CardTitle>
            </div>
            <CardDescription>
              Select a cohort to track this full matrix analysis against actual performance data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CohortSelector
              selectedCohort={selectedCohort}
              onCohortChange={setSelectedCohort}
              placeholder="Select target cohort (optional)"
              showCreateOption={true}
              className="bg-white"
            />
            {selectedCohort && (
              <p className="text-xs text-teal-600 mt-2 flex items-center gap-1">
                <Users size={12} />
                Projections will be tracked for: {selectedCohort.name}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
          {/* Configuration Panel */}
          <div className="space-y-4" ref={configRef}>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Elasticity Scenario</Label>
                  <Select
                    value={config.elasticity_scenario}
                    onValueChange={(value) =>
                      setConfig((prev) => ({ ...prev, elasticity_scenario: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conservative">Conservative</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="aggressive">Aggressive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Total Investment ({getCurrencySymbol(currency)})</Label>
                  <Input
                    type="number"
                    value={config.intervention_cost}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        intervention_cost: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label className="text-xs">Headcount</Label>
                    <Input
                      type="number"
                      value={config.headcount}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          headcount: parseInt(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Rev/Emp</Label>
                    <Input
                      type="number"
                      value={config.revenue_per_employee}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          revenue_per_employee: parseFloat(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Headline + assessment-source indicator —
                      mirrors Single Measure & Portfolio Analysis. */}
                  <div className="flex items-baseline gap-3" data-testid="matrix-maturity-headline">
                    <span className="text-3xl font-extrabold text-slate-900 tracking-tight">
                      {config.current_maturity}%
                    </span>
                    <span className={cn(
                      "text-sm font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full",
                      config.current_maturity <= 25 && "bg-rose-100 text-rose-700",
                      config.current_maturity > 25 && config.current_maturity <= 50 && "bg-amber-100 text-amber-700",
                      config.current_maturity > 50 && config.current_maturity <= 75 && "bg-sky-100 text-sky-700",
                      config.current_maturity > 75 && "bg-emerald-100 text-emerald-700",
                    )}>
                      {config.current_maturity <= 25 ? 'Emerging'
                        : config.current_maturity <= 50 ? 'Developing'
                        : config.current_maturity <= 75 ? 'Managed'
                        : 'Optimized'}
                    </span>
                    <span className="text-xs text-slate-500">value realisation maturity</span>
                  </div>
                  {latestMaturityAssessment && (
                    <div
                      className="inline-flex items-center gap-2 text-[11px] text-slate-600"
                      data-testid="matrix-maturity-source-indicator"
                    >
                      {maturityOverridden ? (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold uppercase tracking-wide">
                            Manual override
                          </span>
                          <span>
                            Latest assessment scored {Math.round(latestMaturityAssessment.overall_score)}%
                            {latestMaturityAssessment.maturity_band ? ` (${latestMaturityAssessment.maturity_band})` : ''}
                            {' · '}
                            <button
                              type="button"
                              className="text-teal-600 hover:underline font-semibold"
                              onClick={() => {
                                setConfig(prev => ({ ...prev, current_maturity: Math.round(latestMaturityAssessment.overall_score) }));
                                setMaturityOverridden(false);
                              }}
                              data-testid="matrix-maturity-revert"
                            >
                              Revert to assessment
                            </button>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold uppercase tracking-wide">
                            Synced from Maturity Assessment
                          </span>
                          <button
                            type="button"
                            className="text-teal-600 hover:underline font-semibold"
                            onClick={() => navigate('/maturity-health')}
                            data-testid="matrix-maturity-view-assessment"
                          >
                            View assessment
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  <MaturitySliderWithGuide
                    value={config.current_maturity}
                    onChange={(value) => {
                      setConfig((prev) => ({ ...prev, current_maturity: value }));
                      setMaturityOverridden(true);
                    }}
                    showTitle={false}
                    testId="matrix-maturity-slider"
                  />
                </div>

                {/* Execution Risk Factors */}
                <div className="border-t pt-3">
                  <ExecutionRiskCollapsible
                    values={config.dampening_factors}
                    onChange={(field, value) =>
                      setConfig((prev) => ({
                        ...prev,
                        dampening_factors: { ...prev.dampening_factors, [field]: value }
                      }))
                    }
                    isOpen={showDampeningDetails}
                    onToggle={() => setShowDampeningDetails(!showDampeningDetails)}
                  />
                </div>

                {/* Value Transfer Conditions */}
                <TransferFactorsCard
                  config={config}
                  onConfigChange={(updates) => {
                    setConfig(prev => ({ ...prev, ...updates }));
                    setResultsStale(true);
                  }}
                  headcount={config.headcount}
                  showLearnerPopulation={true}
                  className="mt-4"
                />

                {/* Validation Warning */}
                <Button
                  onClick={handleCalculate}
                  disabled={isCalculating || !hasValidMeasures}
                  className={`w-full ${hasValidMeasures ? 'bg-navy-700 hover:bg-navy-800' : 'bg-slate-400 cursor-not-allowed'}`}
                  data-testid="calculate-matrix-btn"
                >
                  {isCalculating ? (
                    <>
                      <div className="spinner mr-2" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="mr-2 h-4 w-4" />
                      Calculate Matrix
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Measures Quick Config */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">LEI Measures ({enabledCount}/{leiMeasures.length})</CardTitle>
                {/* Bulk Value Apply */}
                <div className="flex items-center gap-2 mt-2 p-2 bg-slate-50 rounded-lg">
                  <span className="text-xs text-slate-600">Set all:</span>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={bulkValue}
                    onChange={(e) => setBulkValue(parseFloat(e.target.value) || 0)}
                    className="w-16 h-7 text-xs text-center"
                    data-testid="bulk-value-input-matrix"
                  />
                  <span className="text-xs text-slate-600">%</span>
                  <Button
                    size="sm"
                    onClick={applyBulkValue}
                    className="bg-teal-600 hover:bg-teal-700 h-7 text-xs px-2"
                    data-testid="apply-bulk-btn-matrix"
                  >
                    Apply
                  </Button>
                </div>
                
                {/* Validation Warning */}
                {!hasValidMeasures && enabledCount > 0 && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                    <AlertTriangle className="h-3 w-3 text-amber-600 flex-shrink-0" />
                    <p className="text-[10px] text-amber-800">
                      Enter at least one measure with improvement &gt; 0%
                    </p>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                  {leiMeasures.map((measure) => {
                    const input = measureInputs.find(
                      (m) => m.lei_measure_id === measure.id
                    );
                    return (
                      <div
                        key={measure.id}
                        className="flex items-center gap-2 p-2 rounded bg-slate-50"
                      >
                        <Switch
                          checked={input?.enabled || false}
                          onCheckedChange={(checked) =>
                            updateMeasureInput(measure.id, 'enabled', checked)
                          }
                        />
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <span className="text-xs truncate">{measure.name}</span>
                          <LEIMeasureInfo measure={measure} iconSize="sm" />
                        </div>
                        {/* Calculator Button - visible in advanced mode */}
                        {analysisMode === 'advanced' && measure.lei_formula && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => openCalculator(measure)}
                                data-testid={`calculator-matrix-${measure.id}`}
                              >
                                <FlaskConical className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Open LEI Calculator</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {/* Import baseline indicator */}
                        {analysisMode === 'import' && importedBaselines?.baselines?.[measure.id] && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded font-medium" title="Imported baseline score">
                            {importedBaselines.baselines[measure.id].calculated_score?.toFixed(1)}
                          </span>
                        )}
                        <Input
                          type="number"
                          min="0"
                          max="20"
                          step="0.5"
                          value={input?.lei_improvement || 0}
                          onChange={(e) =>
                            updateMeasureInput(
                              measure.id,
                              'lei_improvement',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          disabled={!input?.enabled}
                          className="w-14 h-7 text-xs text-center"
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="xl:col-span-3 space-y-6">
            {/* View Toggle - Always visible */}
            <div className="flex justify-between items-center">
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={viewMode === 'sankey' ? 'default' : 'outline'}
                  onClick={() => setViewMode('sankey')}
                  className={viewMode === 'sankey' ? 'bg-navy-700' : ''}
                  disabled={!results && viewMode !== 'sankey'}
                >
                  <GitBranch className="mr-2 h-4 w-4" />
                  Sankey Flow
                </Button>
                <Button
                  variant={viewMode === 'by-kpi' ? 'default' : 'outline'}
                  onClick={() => setViewMode('by-kpi')}
                  className={viewMode === 'by-kpi' ? 'bg-navy-700' : ''}
                  disabled={!results && viewMode !== 'by-kpi'}
                >
                  <Target className="mr-2 h-4 w-4" />
                  By KPI
                </Button>
                <Button
                  variant={viewMode === 'by-measure' ? 'default' : 'outline'}
                  onClick={() => setViewMode('by-measure')}
                  className={viewMode === 'by-measure' ? 'bg-navy-700' : ''}
                  disabled={!results && viewMode !== 'by-measure'}
                >
                  <TrendingUp className="mr-2 h-4 w-4" />
                  By Measure
                </Button>
                <Button
                  variant={viewMode === 'weights' ? 'default' : 'outline'}
                  onClick={() => setViewMode('weights')}
                  className={viewMode === 'weights' ? 'bg-navy-700' : ''}
                >
                  <Grid3X3 className="mr-2 h-4 w-4" />
                  Weight Matrix
                </Button>
              </div>
              {results && (
                <div className="flex gap-2">
                  <SaveScenarioDialog 
                    defaultName="Full Impact Matrix Analysis"
                    analysisType="matrix"
                    trigger={
                      <Button variant="outline" data-testid="save-matrix-btn">
                        <Save className="mr-2 h-4 w-4" />
                        Save Scenario
                      </Button>
                    }
                  />
                  <BoardReadyButton 
                    analysisType="matrix"
                    defaultName="Full Impact Matrix Analysis"
                  />
                </div>
              )}
            </div>
            
            {/* Stale Results Warning */}
            {results && resultsStale && (
              <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Configuration has changed
                  </p>
                  <p className="text-xs text-amber-600">
                    The results below are from your previous calculation. Click "Calculate Full Matrix" to update with your new values.
                  </p>
                </div>
                <Button 
                  onClick={handleCalculate}
                  disabled={isCalculating}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                >
                  {isCalculating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <Calculator className="mr-2 h-4 w-4" />
                      Recalculate
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {results ? (
              <div ref={resultsRef}>
                {/* Calculation Methodology Notes */}
                {results.calculation_notes && results.calculation_notes.length > 0 && (
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardContent className="pt-4 pb-3">
                      <div className="flex items-start gap-2">
                        <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-blue-800 mb-1">Calculation Methodology</p>
                          <ul className="text-xs text-blue-700 space-y-0.5">
                            {results.calculation_notes.map((note, idx) => (
                              <li key={`note-${idx}-${note.slice(0,12)}`}>{'\u2022'} {note}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Summary KPIs - Colorful Gradient Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card className="bg-gradient-to-br from-teal-500 to-teal-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="h-4 w-4" />
                        <p className="text-sm text-teal-100">Total Financial Impact</p>
                        <CalcInfoButton 
                          title="Financial Impact Calculation"
                          breakdown={{ formula: "Σ (KPI Baseline × KPI Δ%)" }}
                          iconClassName="text-teal-700 hover:text-white"
                        >
                          <div className="space-y-1 text-slate-600">
                            <p>Sum of financial impact across all {kpis.length} KPIs</p>
                            <p>Total Baseline: {formatCurrency(results.total_baseline_value, currency)}</p>
                            <p>Portfolio Dampening: {results.portfolio_dampening}</p>
                            <p>Max KPI Uplift: {results.max_kpi_uplift}%</p>
                          </div>
                        </CalcInfoButton>
                      </div>
                      <p className="text-3xl font-bold">
                        {formatCurrency(results.total_financial_impact, currency)}
                      </p>
                      <p className="text-xs text-teal-700 mt-1">
                        Across all {kpis.length} KPIs
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-navy-600 to-navy-700 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-4 w-4" />
                        <p className="text-sm text-slate-200">Total ROI</p>
                        <CalcInfoButton 
                          title="ROI Calculation"
                          breakdown={{ formula: "ROI = (Impact - Cost) / Cost" }}
                          iconClassName="text-slate-300 hover:text-white"
                        >
                          <div className="space-y-1 text-slate-600">
                            <p>Financial Impact: {formatCurrency(results.total_financial_impact, currency)}</p>
                            <p>Investment Cost: {formatCurrency(config.intervention_cost, currency)}</p>
                            <p>Net Benefit: {formatCurrency(results.total_net_benefit, currency)}</p>
                          </div>
                        </CalcInfoButton>
                      </div>
                      <p className="text-3xl font-bold">
                        {(results.total_roi_percent / 100).toFixed(1)}x
                      </p>
                      <p className="text-xs text-slate-300 mt-1">
                        Net: {formatCurrency(results.total_net_benefit, currency)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="h-4 w-4" />
                        <p className="text-sm text-emerald-100">Mean ROI (MC)</p>
                        <CalcInfoButton 
                          title="Monte Carlo Simulation"
                          breakdown={{ formula: "5,000 iterations with randomized inputs" }}
                          iconClassName="text-emerald-700 hover:text-white"
                        >
                          <div className="space-y-2 text-slate-600">
                            <p className="font-medium text-slate-700">What is Monte Carlo simulation?</p>
                            <p className="text-xs">We run 5,000 scenarios with randomized assumptions to show the range of possible outcomes - not just one "best guess."</p>
                            <div className="border-t pt-2 mt-2">
                              <p className="text-xs"><strong>Mean ROI:</strong> Average across all simulations</p>
                              <p className="text-xs"><strong>P5 (Downside):</strong> {(results.roi_p5 / 100).toFixed(1)}x - worst 5% of scenarios</p>
                              <p className="text-xs"><strong>P95 (Upside):</strong> {(results.roi_p95 / 100).toFixed(1)}x - best 5% of scenarios</p>
                            </div>
                            <div className="border-t pt-2 mt-2 text-xs">
                              <p><strong>Inputs varied:</strong></p>
                              <p>• Elasticity: Normal distribution (±20%)</p>
                              <p>• Dampening: Triangular distribution</p>
                            </div>
                          </div>
                        </CalcInfoButton>
                      </div>
                      <p className="text-3xl font-bold">
                        {(results.roi_mean / 100).toFixed(1)}x
                      </p>
                      <p className="text-xs text-emerald-700 mt-1">
                        P5: {(results.roi_p5 / 100).toFixed(1)}x / P95: {(results.roi_p95 / 100).toFixed(1)}x
                      </p>
                      {Math.abs((results.roi_p95 || 0) - (results.roi_p5 || 0)) <= 10 && (
                        <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-semibold text-teal-800" data-testid="fullmatrix-mc-confidence-badge">
                          <span className="h-1.5 w-1.5 rounded-full bg-teal-600" /> High confidence · tight band
                        </span>
                      )}
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="h-4 w-4" />
                        <p className="text-sm text-purple-100">Prob. Positive ROI</p>
                      </div>
                      <p className="text-3xl font-bold">
                        {results.prob_positive_roi.toFixed(1)}%
                      </p>
                      <p className="text-xs text-purple-700 mt-1">
                        5,000 Monte Carlo iterations
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4" />
                        <p className="text-sm text-amber-100">Payback Period</p>
                      </div>
                      <p className="text-3xl font-bold">
                        {results.payback_months != null 
                          ? `${results.payback_months.toFixed(1)} mo`
                          : 'N/A'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Projected LEI impact */}
                {results.lei_impact && (
                  <LeiImpactCard leiImpact={results.lei_impact} testId="matrix-lei-impact" />
                )}

                {/* Sankey View */}
                {viewMode === 'sankey' && sankeyData && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <GitBranch className="h-5 w-5 text-teal-600" />
                        LEI Measures → KPIs Flow
                      </CardTitle>
                      <CardDescription>
                        Showing top 20 impact flows by financial contribution
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ErrorBoundary fallback={
                        <div className="h-[500px] flex items-center justify-center bg-slate-50 rounded-lg">
                          <div className="text-center">
                            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-2" />
                            <p className="text-slate-600 font-medium">Unable to render Sankey diagram</p>
                            <p className="text-sm text-slate-500 mt-1">Try viewing results in "By KPI" or "By Measure" tabs</p>
                          </div>
                        </div>
                      }>
                        <div className="w-full" style={{ minHeight: '620px' }}>
                          <Sankey
                            width={1100}
                            height={600}
                            data={sankeyData}
                            nodeWidth={10}
                            nodePadding={32}
                            margin={{ top: 20, right: 220, bottom: 20, left: 260 }}
                            link={<CustomSankeyLink />}
                            node={<CustomSankeyNode />}
                          />
                        </div>
                      </ErrorBoundary>
                      <div className="mt-4 flex justify-center gap-8 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded bg-teal-600" />
                          <span className="text-slate-600">LEI Measures (Source)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ArrowRight className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-600">Impact Flow</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded bg-navy-700" />
                          <span className="text-slate-600">Business KPIs (Target)</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* By KPI View */}
                {viewMode === 'by-kpi' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Impact by KPI</CardTitle>
                      <CardDescription>
                        Financial impact and attributed ROI for each business KPI (with KPI-specific baselines)
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {results.kpi_impacts?.map((kpi) => (
                          <div
                            key={kpi.kpi_id}
                            className={`p-4 rounded-lg border ${kpi.ceiling_applied ? 'border-amber-300 bg-amber-50/50' : 'border-slate-200 bg-slate-50'}`}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-slate-900">{kpi.kpi_name}</h4>
                                  <CalcInfoButton 
                                    title={`${kpi.kpi_name} Calculation`}
                                    breakdown={kpi.calculation_breakdown}
                                  >
                                    <div className="space-y-1.5 text-slate-600">
                                      <p><strong>Baseline:</strong> ${kpi.calculation_breakdown?.baseline_per_employee}/employee × {kpi.calculation_breakdown?.headcount} = {formatCurrency(kpi.baseline_value, currency)}</p>
                                      <p><strong>Raw Δ:</strong> {kpi.total_kpi_delta_percent_raw?.toFixed(3)}%</p>
                                      {kpi.ceiling_applied && (
                                        <p className="text-amber-600"><strong>Ceiling Applied:</strong> Capped at {results.max_kpi_uplift}%</p>
                                      )}
                                      <p><strong>Final Δ:</strong> {kpi.total_kpi_delta_percent?.toFixed(3)}%</p>
                                      <p><strong>Impact:</strong> {formatCurrency(kpi.baseline_value, currency)} × {kpi.total_kpi_delta_percent?.toFixed(3)}% = {formatCurrency(kpi.total_financial_impact, currency)}</p>
                                    </div>
                                  </CalcInfoButton>
                                  {kpi.ceiling_applied && (
                                    <span className="text-xs px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded">
                                      Ceiling Applied
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">{kpi.kpi_description}</p>
                                <p className="text-sm text-slate-500">
                                  {kpi.contributing_measures?.length || 0} contributing measures • Baseline: {formatCurrency(kpi.baseline_value, currency)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-teal-600">
                                  {formatCurrency(kpi.total_financial_impact, currency)}
                                </p>
                                <p className="text-sm text-slate-500">
                                  Δ {kpi.total_kpi_delta_percent?.toFixed(2)}% • ROI: <span className="font-semibold text-navy-700">
                                    {(kpi.attributed_roi_percent / 100).toFixed(1)}x
                                  </span>
                                </p>
                              </div>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-teal-500 to-navy-600 rounded-full"
                                style={{
                                  width: `${Math.min((kpi.total_financial_impact / results.total_financial_impact) * 100, 100)}%`
                                }}
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {((kpi.total_financial_impact / results.total_financial_impact) * 100).toFixed(1)}% of total impact
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* By Measure View */}
                {viewMode === 'by-measure' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Impact by Measure</CardTitle>
                      <CardDescription>
                        How each LEI measure contributes across all KPIs
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {results.measure_impacts?.map((measure) => (
                          <div
                            key={measure.lei_measure_id}
                            className="p-4 rounded-lg border border-slate-200 bg-slate-50"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <h4 className="font-semibold text-slate-900">{measure.lei_measure_name}</h4>
                                <p className="text-xs text-slate-500">{measure.pillar}</p>
                                <p className="text-sm text-teal-600 font-medium mt-1">
                                  +{measure.lei_improvement}% improvement
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-navy-700">
                                  {formatCurrency(measure.total_financial_impact, currency)}
                                </p>
                                <p className="text-xs text-slate-500">
                                  across {measure.kpi_contributions?.length || 0} KPIs
                                </p>
                              </div>
                            </div>
                            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-teal-500 to-teal-700 rounded-full"
                                style={{
                                  width: `${Math.min((measure.total_financial_impact / results.total_financial_impact) * 100, 100)}%`
                                }}
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {((measure.total_financial_impact / results.total_financial_impact) * 100).toFixed(1)}% of total impact
                            </p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Weight Matrix View */}
                {viewMode === 'weights' && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Grid3X3 className="h-5 w-5 text-teal-600" />
                        LEI-KPI Influence Weight Matrix
                      </CardTitle>
                      <CardDescription>
                        Influence weights (0-1) showing how strongly each LEI measure impacts each KPI. 
                        Higher values indicate stronger causal relationships.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-2 font-semibold text-slate-700 sticky left-0 bg-white min-w-[180px]">
                                LEI Measure
                              </th>
                              {kpis.map((kpi) => (
                                <th key={kpi.id} className="text-center py-2 px-1 font-semibold text-slate-700 min-w-[70px]">
                                  <div className="truncate max-w-[70px]" title={kpi.name}>
                                    {kpi.name.length > 10 ? kpi.name.substring(0, 8) + '...' : kpi.name}
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {leiMeasures.map((measure) => (
                              <tr key={measure.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-2 font-medium text-slate-700 sticky left-0 bg-white">
                                  <div className="truncate max-w-[180px]" title={measure.name}>
                                    {measure.name}
                                  </div>
                                  <div className="text-[10px] text-slate-400">{measure.pillar}</div>
                                </td>
                                {kpis.map((kpi) => {
                                  const weight = weightMatrix[measure.id]?.[kpi.id] || 0;
                                  // Color scale: lower weights lighter, higher weights darker
                                  const intensity = Math.round(weight * 100);
                                  let bgColor = 'bg-slate-50';
                                  let textColor = 'text-slate-400';
                                  if (weight >= 0.9) {
                                    bgColor = 'bg-teal-600';
                                    textColor = 'text-white';
                                  } else if (weight >= 0.8) {
                                    bgColor = 'bg-teal-500';
                                    textColor = 'text-white';
                                  } else if (weight >= 0.7) {
                                    bgColor = 'bg-teal-400';
                                    textColor = 'text-white';
                                  } else if (weight >= 0.6) {
                                    bgColor = 'bg-teal-300';
                                    textColor = 'text-slate-800';
                                  } else if (weight >= 0.5) {
                                    bgColor = 'bg-teal-200';
                                    textColor = 'text-slate-700';
                                  }
                                  return (
                                    <td key={kpi.id} className="text-center py-1 px-1">
                                      <div className={`rounded px-1 py-1 ${bgColor} ${textColor} font-mono`}>
                                        {weight.toFixed(2)}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-600">
                        <span className="font-medium">Weight Scale:</span>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-4 rounded bg-slate-100 border border-slate-200" />
                          <span>0.5</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-4 rounded bg-teal-200" />
                          <span>0.5-0.6</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-4 rounded bg-teal-300" />
                          <span>0.6-0.7</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-4 rounded bg-teal-400" />
                          <span>0.7-0.8</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-4 rounded bg-teal-500" />
                          <span>0.8-0.9</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-6 h-4 rounded bg-teal-600" />
                          <span>0.9+</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Warnings with Actionable Guidance */}
                {results && results.warnings && results.warnings.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="h-5 w-5 text-amber-600" />
                            <p className="font-semibold text-amber-800">Analysis Notes</p>
                          </div>
                          
                          {/* Warning messages */}
                          <ul className="space-y-2 text-sm text-amber-700 mb-4">
                            {results.warnings.map((warning, idx) => (
                              <li key={`warn-${idx}-${warning.slice(0,15)}`} className="flex items-start gap-2">
                                <span className="text-amber-500 mt-0.5">•</span>
                                <span>{warning}</span>
                              </li>
                            ))}
                          </ul>
                          
                          {/* Detailed explanation and guidance */}
                          {results.warning_details && results.warning_details.length > 0 && (
                            <div className="space-y-4">
                              {results.warning_details.map((detail, idx) => (
                                <div key={detail.explanation?.slice(0,20) || `detail-${idx}`} className="p-4 bg-white border border-slate-200 rounded-lg border border-amber-200">
                                  {/* Explanation */}
                                  <p className="text-sm text-slate-700 mb-3">{detail.explanation}</p>
                                  
                                  {/* Typical ROI Ranges - Left justified */}
                                  {detail.typical_ranges && (
                                    <div className="mb-4">
                                      <p className="text-xs font-semibold text-amber-800 mb-2 underline">Typical ROI Ranges:</p>
                                      <div className="space-y-0.5">
                                        {Object.entries(detail.typical_ranges).map(([key, value]) => (
                                          <div key={key} className="flex text-sm">
                                            <span className="capitalize text-amber-600 w-28">{key}:</span>
                                            <span className="font-mono text-amber-700">{value}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Actionable Guidance */}
                                  {detail.guidance && detail.guidance.length > 0 && (
                                    <div>
                                      <p className="text-xs font-semibold text-amber-800 mb-2">Suggested Actions:</p>
                                      <ul className="space-y-1">
                                        {detail.guidance.map((tip, tipIdx) => (
                                          <li key={`tip-${tipIdx}-${String(tip).slice(0,24)}`} className="text-xs text-amber-700 flex items-start gap-2">
                                            <span className="text-amber-400 mt-0.5">→</span>
                                            <span>{tip}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={scrollToConfig}
                          className="flex-shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Settings className="mr-1 h-4 w-4" />
                          Adjust Parameters
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* ========== HOW WE GOT HERE SECTION ========== */}
                {results && (
                  <div className="space-y-4 mt-6">
                    <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
                      <BarChart3 className="h-5 w-5 text-navy-700" />
                      <h2 className="text-xl font-bold text-slate-900">How We Got Here</h2>
                      <span className="text-sm text-slate-500 ml-2">Impact contribution breakdown</span>
                    </div>

                    {/* Transform measure_impacts to match MeasureContributionChart format */}
                    {/* Calculate total KPI delta from kpi_contributions */}
                    <MeasureContributionChart
                      measureResults={results.measure_impacts?.map(m => {
                        // Sum up all KPI deltas from kpi_contributions
                        // Note: API returns 'contribution_percent' not 'kpi_delta_percent'
                        const totalKpiDelta = m.kpi_contributions?.reduce(
                          (sum, kpi) => sum + (kpi.contribution_percent || kpi.kpi_delta_percent || 0), 0
                        ) || 0;
                        return {
                          lei_measure_id: m.lei_measure_id,
                          lei_measure_name: m.lei_measure_name,
                          pillar: m.pillar,
                          lei_improvement: m.lei_improvement,
                          kpi_delta_percent: totalKpiDelta,
                          financial_impact: m.total_financial_impact,
                          weight: m.avg_weight || 0.7,
                        };
                      }) || []}
                      totalKpiDelta={results.measure_impacts?.reduce((sum, m) => {
                        const measureKpiDelta = m.kpi_contributions?.reduce(
                          (s, kpi) => s + (kpi.contribution_percent || kpi.kpi_delta_percent || 0), 0
                        ) || 0;
                        return sum + measureKpiDelta;
                      }, 0) || 0}
                      totalFinancialImpact={results.total_financial_impact}
                    />

                    {/* Pie Chart and Calculation Methodology */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <MeasureSharePieChart
                        measureResults={results.measure_impacts?.map(m => ({
                          lei_measure_id: m.lei_measure_id,
                          lei_measure_name: m.lei_measure_name,
                          financial_impact: m.total_financial_impact,
                        })) || []}
                        totalFinancialImpact={results.total_financial_impact}
                        title="Financial Impact Share"
                      />
                      
                      {/* Calculation Notes Card */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base font-heading">Calculation Methodology</CardTitle>
                          <CardDescription className="text-xs">
                            Key factors applied to this analysis
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3 text-sm">
                            {results.calculation_notes?.map((note, idx) => (
                              <div key={`calcnote-${idx}-${note.slice(0,15)}`} className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 flex-shrink-0" />
                                <span className="text-slate-600">{note}</span>
                              </div>
                            ))}
                            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                              <p className="text-xs font-medium text-slate-700">Formula Applied:</p>
                              <p className="text-xs font-mono text-slate-600 mt-1">
                                KPI Delta = LEI% × Weight × Elasticity × Dampening × Maturity
                              </p>
                              <p className="text-xs font-mono text-slate-600 mt-1">
                                Financial Impact = KPI Delta × Baseline × <span className="text-amber-600 font-semibold">Combined Adjustment</span>
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Impact Adjustments Card - Collapsible */}
                    {results && (
                      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
                        <Collapsible open={isImpactAdjustmentsExpanded} onOpenChange={setIsImpactAdjustmentsExpanded}>
                          <CollapsibleTrigger asChild>
                            <CardHeader className="pb-2 cursor-pointer hover:bg-amber-100/50 transition-colors">
                              <div className="flex items-center justify-between">
                                <div>
                                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                                    <User className="h-4 w-4 text-amber-600" />
                                    Impact Adjustments Applied
                                    {isImpactAdjustmentsExpanded ? (
                                      <ChevronUp className="h-4 w-4 text-amber-400" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4 text-amber-400" />
                                    )}
                                  </CardTitle>
                                  {!isImpactAdjustmentsExpanded && (
                                    <CardDescription className="text-sm text-muted-foreground">
                                      Click to view factors that adjust theoretical impact
                                    </CardDescription>
                                  )}
                                </div>
                                {results.combined_adjustment_factor && (
                                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 text-amber-700">
                                    <div className="text-right">
                                      <p className="text-lg font-bold">{(results.combined_adjustment_factor * 100).toFixed(1)}%</p>
                                      <p className="text-xs font-medium">Combined Adjustment</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </CardHeader>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <CardContent>
                              <div className="space-y-4">
                                {/* Combined Adjustment Factor */}
                                {results.combined_adjustment_factor && (
                                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-300">
                                    <div>
                                      <p className="text-sm font-medium text-slate-700">Combined Adjustment</p>
                                      <p className="text-xs text-slate-500">All factors multiplied</p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-2xl font-bold text-amber-600">
                                        {(results.combined_adjustment_factor * 100).toFixed(1)}%
                                      </p>
                                      <p className="text-xs text-slate-500">of theoretical impact</p>
                                    </div>
                                  </div>
                                )}

                                {/* Individual Factors Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                  {results.scale_dampening && (
                                    <div className="p-3 bg-white rounded-lg border border-slate-200">
                                      <div className="flex justify-between items-start mb-1">
                                        <p className="text-xs font-medium text-slate-600">Scale Dampening</p>
                                        <span className="text-sm font-bold text-amber-600">
                                          {(results.scale_dampening.factor * 100).toFixed(0)}%
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-500">{results.scale_dampening.bracket}</p>
                                    </div>
                                  )}
                                  {results.industry_modifier && (
                                    <div className="p-3 bg-white rounded-lg border border-slate-200">
                                      <div className="flex justify-between items-start mb-1">
                                        <p className="text-xs font-medium text-slate-600">Industry</p>
                                        <span className="text-sm font-bold text-blue-600">
                                          {(results.industry_modifier.modifier * 100).toFixed(0)}%
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-500">{results.industry_modifier.industry}</p>
                                    </div>
                                  )}
                                  {results.learning_modality && (
                                    <div className="p-3 bg-white rounded-lg border border-slate-200">
                                      <div className="flex justify-between items-start mb-1">
                                        <p className="text-xs font-medium text-slate-600">Learning Modality</p>
                                        <span className="text-sm font-bold text-purple-600">
                                          {(results.learning_modality.factor * 100).toFixed(0)}%
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-500">{results.learning_modality.modality}</p>
                                    </div>
                                  )}
                                  {results.time_lag && (
                                    <div className="p-3 bg-white rounded-lg border border-slate-200">
                                      <div className="flex justify-between items-start mb-1">
                                        <p className="text-xs font-medium text-slate-600">Time Lag</p>
                                        <span className="text-sm font-bold text-teal-600">
                                          {(results.time_lag.factor * 100).toFixed(0)}%
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-500">{results.time_lag.effective_months} effective months</p>
                                    </div>
                                  )}
                                </div>

                                {/* Capability Transfer Rate */}
                                {results.transfer_rate && (
                                  <TransferRateResultsCard 
                                    transferRate={results.transfer_rate} 
                                    transferMethod={results.transfer_method}
                                    roeCompositeDetail={results.transfer_method === 'roe_composite' ? results.transfer_rate : null}
                                    className="mt-4" 
                                  />
                                )}

                                {/* Raw vs Adjusted Comparison */}
                                {results.total_financial_impact_raw && results.combined_adjustment_factor < 1 && (
                                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-amber-200">
                                    <div className="p-2 bg-white rounded border border-slate-200">
                                      <p className="text-xs text-slate-500">Theoretical Impact</p>
                                      <p className="text-sm font-mono font-semibold text-slate-400 line-through">
                                        {formatCurrency(results.total_financial_impact_raw, currency)}
                                      </p>
                                    </div>
                                    <div className="p-2 bg-white rounded border border-amber-300">
                                      <p className="text-xs text-amber-600">Realistic Impact</p>
                                      <p className="text-sm font-mono font-semibold text-amber-700">
                                        {formatCurrency(results.total_financial_impact, currency)}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </CollapsibleContent>
                        </Collapsible>
                      </Card>
                    )}

                    {/* Detailed Measure Breakdown Table - Collapsible */}
                    <Card>
                      <Collapsible open={isDetailedBreakdownExpanded} onOpenChange={setIsDetailedBreakdownExpanded}>
                        <CollapsibleTrigger asChild>
                          <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="text-base font-semibold flex items-center gap-2">
                                  <BarChart3 className="h-4 w-4 text-slate-600" />
                                  Detailed Breakdown by Measure
                                  {isDetailedBreakdownExpanded ? (
                                    <ChevronUp className="h-4 w-4 text-slate-400" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4 text-slate-400" />
                                  )}
                                </CardTitle>
                                {!isDetailedBreakdownExpanded && (
                                  <CardDescription className="text-sm text-muted-foreground">
                                    Click to view individual contribution of each LEI measure across all KPIs
                                  </CardDescription>
                                )}
                              </div>
                              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-slate-700">
                                <div className="text-right">
                                  <p className="text-xs font-medium">{results.measure_impacts?.length || 0} Measures</p>
                                  <p className="text-lg font-bold">{formatCurrency(results.total_financial_impact, currency)}</p>
                                </div>
                              </div>
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <CardContent>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-slate-200">
                                    <th className="text-left py-3 px-4 font-semibold text-slate-600">
                                      LEI Measure
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold text-slate-600">
                                      Pillar
                                    </th>
                                    <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                      Improvement
                                    </th>
                                    <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                      KPIs Impacted
                                    </th>
                                    <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                      Financial Impact
                                    </th>
                                    <th className="text-right py-3 px-4 font-semibold text-slate-600">
                                      % of Total
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {results.measure_impacts?.map((mr, idx) => (
                                    <tr
                                      key={mr.lei_measure_id}
                                      className={idx % 2 === 0 ? 'bg-slate-50' : ''}
                                    >
                                      <td className="py-3 px-4 font-medium text-slate-900">
                                        {mr.lei_measure_name}
                                      </td>
                                      <td className="py-3 px-4 text-slate-600 text-xs">
                                        {mr.pillar}
                                      </td>
                                      <td className="py-3 px-4 text-right font-mono">
                                        {mr.lei_improvement.toFixed(1)}%
                                      </td>
                                      <td className="py-3 px-4 text-right font-mono text-slate-600">
                                        {mr.kpi_contributions?.length || kpis.length}
                                      </td>
                                      <td className="py-3 px-4 text-right font-mono text-teal-600 font-semibold">
                                        {formatCurrency(mr.total_financial_impact, currency)}
                                      </td>
                                      <td className="py-3 px-4 text-right font-mono text-navy-600">
                                        {((mr.total_financial_impact / results.total_financial_impact) * 100).toFixed(1)}%
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="border-t-2 border-slate-300 bg-slate-100">
                                    <td className="py-3 px-4 font-bold text-slate-900" colSpan="4">
                                      Total
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono font-bold text-teal-700">
                                      {formatCurrency(results.total_financial_impact, currency)}
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono font-bold text-navy-700">
                                      100%
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  </div>
                )}
              </div>
            ) : viewMode === 'weights' ? (
              <>
                {/* Weight Matrix View - Always accessible */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Grid3X3 className="h-5 w-5 text-teal-600" />
                      LEI-KPI Influence Weight Matrix
                    </CardTitle>
                    <CardDescription>
                      Influence weights (0-1) showing how strongly each LEI measure impacts each KPI. 
                      Higher values indicate stronger causal relationships.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-2 font-semibold text-slate-700 sticky left-0 bg-white min-w-[180px]">
                              LEI Measure
                            </th>
                            {kpis.map((kpi) => (
                              <th key={kpi.id} className="text-center py-2 px-1 font-semibold text-slate-700 min-w-[70px]">
                                <div className="truncate max-w-[70px]" title={kpi.name}>
                                  {kpi.name.length > 10 ? kpi.name.substring(0, 8) + '...' : kpi.name}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {leiMeasures.map((measure) => (
                            <tr key={measure.id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2 px-2 font-medium text-slate-700 sticky left-0 bg-white">
                                <div className="truncate max-w-[180px]" title={measure.name}>
                                  {measure.name}
                                </div>
                                <div className="text-[10px] text-slate-400">{measure.pillar}</div>
                              </td>
                              {kpis.map((kpi) => {
                                const weight = weightMatrix[measure.id]?.[kpi.id] || 0;
                                let bgColor = 'bg-slate-50';
                                let textColor = 'text-slate-400';
                                if (weight >= 0.9) {
                                  bgColor = 'bg-teal-600';
                                  textColor = 'text-white';
                                } else if (weight >= 0.8) {
                                  bgColor = 'bg-teal-500';
                                  textColor = 'text-white';
                                } else if (weight >= 0.7) {
                                  bgColor = 'bg-teal-400';
                                  textColor = 'text-white';
                                } else if (weight >= 0.6) {
                                  bgColor = 'bg-teal-300';
                                  textColor = 'text-slate-800';
                                } else if (weight >= 0.5) {
                                  bgColor = 'bg-teal-200';
                                  textColor = 'text-slate-700';
                                }
                                return (
                                  <td key={kpi.id} className="text-center py-1 px-1">
                                    <div className={`rounded px-1 py-1 ${bgColor} ${textColor} font-mono`}>
                                      {weight.toFixed(2)}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-600 flex-wrap">
                      <span className="font-medium">Weight Scale:</span>
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-4 rounded bg-slate-100 border border-slate-200" />
                        <span>&lt;0.5</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-4 rounded bg-teal-200" />
                        <span>0.5-0.6</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-4 rounded bg-teal-300" />
                        <span>0.6-0.7</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-4 rounded bg-teal-400" />
                        <span>0.7-0.8</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-4 rounded bg-teal-500" />
                        <span>0.8-0.9</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-6 h-4 rounded bg-teal-600" />
                        <span>0.9+</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="border-dashed">
                <CardContent className="py-16">
                  <div className="text-center">
                    <GitBranch className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900">No Analysis Results</h3>
                    <p className="text-slate-500 mt-2 max-w-md mx-auto">
                      Configure your LEI measures and click "Calculate Matrix" to see the full 
                      impact flow from all measures to all KPIs.
                    </p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={() => setViewMode('weights')}
                    >
                      <Grid3X3 className="mr-2 h-4 w-4" />
                      View Weight Matrix
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tell Me What This Means - AI Narrative */}
            {results && (
              <NarrativeCard
                results={results}
                configuration={{
                  ...config,
                  measures: measureInputs,
                  lei_improvement: measureInputs.filter(m => m.enabled).reduce((sum, m) => sum + (m.lei_improvement || 0), 0),
                  kpi_ids: kpis.map(k => k.id),
                }}
                analysisType="matrix"
                measureName={`${measureInputs.filter(m => m.enabled).length} LEI Measures (Full Matrix)`}
                kpiNames={`${kpis.length} Business KPIs`}
                currency={currency}
                onNarrativeGenerated={setNarrative}
              />
            )}

            {/* Industry Benchmark Comparison */}
            {results && (
              <BenchmarkComparison 
                results={{
                  roi_percent: results.total_roi_percent,
                  kpi_delta_percent: results.total_kpi_delta_percent,
                  payback_months: results.payback_months,
                  financial_impact: results.total_financial_impact,
                }}
                configuration={{
                  lei_improvement: measureInputs.reduce((sum, m) => sum + (m.lei_improvement || 0), 0) / Math.max(measureInputs.length, 1),
                  intervention_cost: measureInputs.reduce((sum, m) => sum + (m.intervention_cost || 0), 0),
                }}
                measures={leiMeasures}
              />
            )}
          </div>
        </div>

        {/* LEI Calculator Drawer */}
        <LEICalculatorDrawer
          open={calculatorOpen}
          onOpenChange={setCalculatorOpen}
          measure={selectedMeasure}
          currentImprovement={
            selectedMeasure 
              ? measureInputs.find(m => m.lei_measure_id === selectedMeasure.id)?.lei_improvement || 0
              : 0
          }
          onApply={handleCalculatorApply}
        />
      </div>
    </TooltipProvider>
    </PageShell>
  );
};

export default FullMatrixPage;
